import {AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {FormBuilder, FormControl, NgForm} from "@angular/forms";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {ActivatedRoute, Router} from "@angular/router";
import {AuthService} from "../../../shared/services/auth.service";
import {FilterService} from "../../../shared/services/filter.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {CompetitionWzService} from "../../../shared/services/competition-wz.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {CompetitionWzRow} from "../../../shared/interface/competition-wz-row";
import {HttpErrorResponse} from "@angular/common/http";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {WZ_STATUS_RELEASED} from "../../../shared/services/dict.service";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {WarehouseProductListComponent} from "../../warehouse/warehouse-product-list/warehouse-product-list.component";
import {
  CompetitionImportValuesDialogListComponent
} from "../../competition-import/competition-import-values-dialog-list/competition-import-values-dialog-list.component";

export interface CompetitionWzRowWithCheckbox extends CompetitionWzRow {
  checkbox: FormControl;
}

@Component({
  selector: 'app-competition-wz-rows-list',
  templateUrl: './competition-wz-rows-list.component.html',
  styleUrls: ['./competition-wz-rows-list.component.scss']
})
export class CompetitionWzRowsListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly WZ_STATUS_RELEASED = WZ_STATUS_RELEASED;

  wzDataSource: CompetitionWzRowWithCheckbox[] = [];

  initialFilters: any;
  displayedColumns: string[] = [
    'checkbox',
    'awardId',
    'amount',
    'protocol',
    'laureate',
    'competitionSettledOn',
    'assetsRegional',
    'competition',
    'status',
    'actions',
  ];

  competitionWzId?: string = null;
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  competitionWzStatus: string | null = null;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private route: ActivatedRoute,
    private competitionWzService: CompetitionWzService,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private fb: FormBuilder,
    private snack: MatSnackBar,
    public dialog: MatDialog,
  ) {
    this.userPermissions = auth.getUserPermissions();

    this.competitionWzService.importRowsListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CompetitionWzRow>) => {
          this.wzDataSource = e?.items?.map(item => ({
            ...item,
            checkbox: this.fb.control(false)
          })) || [];
          this.totalData = e?.pagination?.total;

          if (e?.items && e.items.length > 0) {
            this.competitionWzStatus = e.items[0]?.status?.key;
          } else {
            this.competitionWzStatus = null;
          }
        })
      )
      .subscribe();
    this.parseFilters();
  }

  get isWzReleased(): boolean {
    return this.competitionWzStatus === WZ_STATUS_RELEASED;
  }

  get hasSelectedItems(): boolean {
    return this.wzDataSource.some(item => item.checkbox && item.checkbox.value);
  }
  get hasProtocolItems(): boolean {
    return this.wzDataSource.some(item => item.competitionImportRow?.warehouseProduct?.protocol);
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({}, filters);
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: PageEvent) => {
        this.router.navigate(
          [this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
            page: event.pageIndex,
            pageSize: event.pageSize,
          })]
        );
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.queryParams);

        this.competitionWzId = state.params?.id ?? this.route.snapshot.params?.id;

        if (this.paginator && this.competitionWzId) {
          this.paginator.pageIndex = state.queryParams.page || 0;
          this.paginator.pageSize = state.queryParams.pageSize || 10;
          this.loadListData();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.competitionWzService.getCompetitionWzRows(
      this.competitionWzId,
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters
    );
  }

  filter(): void {
    const formValues = { ...this.filtersForm?.value };

    for (const key in formValues) {
      if (formValues[key] === undefined || formValues[key] === null || formValues[key] === '') {
        delete formValues[key];
      }
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        page: 0,
        pageSize: this.paginator?.pageSize || 10,
        ...formValues
      },
    });
  }

  resetFilters(): void {
    this.filtersForm?.resetForm();

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        page: 0,
        pageSize: this.paginator?.pageSize || 10,
      },
    });
  }

  confirmWz(): void {
    const selectedItems = this.getSelectedItems();
    const competitionWzRowsIds = selectedItems.map(item => item.uuid);

    this.competitionWzService.confirmWZ(competitionWzRowsIds).subscribe({
      next: () => {
        this.snack.open('Pomyślnie zatwierdzono', 'OK', { duration: 3000 });
        this.wzDataSource.forEach(item => item.checkbox.setValue(false));
        this.loadListData();
      },
      error: (error: HttpErrorResponse) => {
        const errors = error.error?.error?.list;
        const errorMessage = errors?.competitionWzRows ?? 'Wystąpił błąd';
        this.snack.open(errorMessage, 'OK', { duration: 5000 });
      }
    });
  }

  getSelectedItems(): CompetitionWzRow[] {
    return this.wzDataSource
      .filter(item => item.checkbox.value)
      .map(({ checkbox, ...item }) => item as CompetitionWzRow);
  }

  delete(competitionWzRow: CompetitionWzRow): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: { text: 'Czy na pewno chcesz usunąć?' },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.competitionWzService.deleteCompetitionWzRow(competitionWzRow.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto', '', { duration: 2000 });
              this.loadListData();
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), { duration: 2000 });
            }
          });
      }
    });
  }

  generatePdf(): void {
    this.competitionWzService.downloadPdf(this.competitionWzId);
  }

  generateProtocols(): void {
    this.competitionWzService.downloadProtocols(this.competitionWzId);
  }

  buildFilters(competitionWzId: string | null): any {
    const filters = { ...this.initialFilters };

    if (competitionWzId !== null && competitionWzId !== undefined) {
      filters.competitionWz = competitionWzId;
    }

    return filters;
  }

  downloadReport(): void {
    const filters = this.buildFilters(this.competitionWzId?.toString() || null);

    this.competitionWzService.downloadRowsReport(filters)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: { jobId: response.uuid, showProgress: false },
        });

        dialogRef.afterClosed().subscribe(() => {
          this.loadListData();
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  addProducts(): void {
    const dialogRef = this.dialog.open(WarehouseProductListComponent, {
      width: '90%',
      data: { competitionWz: this.competitionWzId },
      height: '90%',
      maxHeight: '90%',
      panelClass: 'full-height-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
      this.changed.emit(true);
      this.dialog.closeAll();
    });
  }

  addRows(): void {
    const dialogRef = this.dialog.open(CompetitionImportValuesDialogListComponent, {
      width: '90%',
      data: { competitionWz: this.competitionWzId },
      height: '90%',
      maxHeight: '90%',
      panelClass: 'full-height-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
      this.changed.emit(true);
      this.dialog.closeAll();
    });
  }

}
